// @ts-nocheck
"use client";

import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { Form, Formik } from "formik";
import Link from "next/link";

import { AuthError } from "next-auth";
import { getSession, signIn } from "next-auth/react";
import { useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import LoadingOverlayComponent from "@/app/components/LoadingOverlayComponent";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { loginType } from "@/types/auth/auth";

const AuthLogin = ({ title, subtitle, subtext }: loginType) => {
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	/*
		Where to land after signing in. The proxy puts the blocked path in
		`callbackUrl` when it turns a signed-out visitor away, so signing in
		finishes the navigation the user actually started instead of dropping
		everybody on the dashboard.

		Read off `window.location` rather than `useSearchParams`, which would
		force this page behind a Suspense boundary for a value only ever needed
		after a click.

		Only same-origin paths are honoured. `callbackUrl` arrives in the URL bar
		where anyone can write it, and following an absolute one would make the
		login page an open redirect: a link to our own domain that lands on
		somebody else's, wearing our sign-in as cover. `//evil.com` is a URL, not
		a path, which is why leading `/` alone is not enough of a test.
	*/
	function destinationAfterLogin(): string {
		const requested = new URLSearchParams(window.location.search).get(
			"callbackUrl",
		);
		if (!requested) return "/dashboard";
		if (!requested.startsWith("/") || requested.startsWith("//")) {
			return "/dashboard";
		}
		return requested;
	}

	const validationSchema = Yup.object({
		email: Yup.string()
			.email("Invalid email address")
			.required("Email is required"),
		password: Yup.string()
			.min(6, "Password must be at least 6 characters")
			.required("Password is required"),
	});

	const initialValues = {
		email: "",
		password: "",
		rememberMe: false,
	};

	const [showPassword, setShowPassword] = useState(false);

	async function handleSubmit(values: typeof initialValues) {
		try {
			setError("");
			setIsLoading(true);
			const response = await signIn("credentials", {
				email: values.email,
				password: values.password,
				remember_me: values.rememberMe,
				redirect: false,
				/*
					Passed explicitly, and this is not optional.

					`signIn` defaults `redirectTo` to `window.location.href`, hands
					that to the server as where to go on success, and then reads
					`error` and `code` back off the URL it gets returned. Auth.js
					puts `?error=CredentialsSignin` on this page when a sign-in
					fails, so from the first failure onwards that param is in the
					address bar, gets sent back out as the destination, and comes
					home to be parsed as the verdict on the *next* attempt.

					The result is a login that is stuck: correct credentials sign
					the user in server-side, the session cookie is set, and the form
					still reports the failure from minutes ago. It clears only if
					the user happens to strip the query string by hand.
				*/
				callbackUrl: destinationAfterLogin(),
			});

			if (response?.error) {
				console.error("Login failed:", response);
				toast.error(response.code);
				setError(response.code || "An unexpected error occurred");
				setIsLoading(false);
				return;
			}

			const session = await getSession();

			if (response?.ok && session !== null && (session as any).accessToken) {
				// Keep loading state true during redirect for better UX
				window.location.href = destinationAfterLogin();
			} else {
				setIsLoading(false);
			}
		} catch (error: any) {
			setIsLoading(false);
			if (error instanceof AuthError) {
				setError(error.message);
				return error;
			}
			setError("An unexpected error occurred");
			throw error;
		}
	}

	return (
		<LoadingOverlayComponent active={isLoading}>
			{title ? (
				<h1 className="mb-1 text-2xl leading-8 font-bold">{title}</h1>
			) : null}

			{subtext}

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					isSubmitting,
					setFieldValue,
				}) => (
					<Form>
						{error && (
							<Alert variant="destructive" className="mb-4">
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						{/*
							Spacing comes from the flex gaps and nowhere else.

							It used to come from three places at once: `gap-6` here, a
							`mt-[25px]` on every label, and a `my-4` on the row below. They
							stacked, so a label sat 49px under the field above it and the
							form read as four unrelated blocks rather than one.

							`gap-4` between fields and `gap-2` between a label and its input
							is the pattern the settings forms already use.
						*/}
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-2">
								<Label htmlFor="email">Email</Label>
								<Input
									id="email"
									name="email"
									type="email"
									value={values.email}
									onChange={handleChange}
									onBlur={handleBlur}
									aria-invalid={!!(errors.email && touched.email)}
								/>
								{errors.email && touched.email ? (
									<p className="text-error text-xs">{errors.email}</p>
								) : null}
							</div>

							<div className="flex flex-col gap-2">
								<Label htmlFor="password">Password</Label>
								<div className="relative">
									<Input
										id="password"
										name="password"
										type={showPassword ? "text" : "password"}
										value={values.password}
										onChange={handleChange}
										onBlur={handleBlur}
										aria-invalid={!!(errors.password && touched.password)}
										className="pr-10"
									/>
									<button
										type="button"
										aria-label={
											showPassword ? "Hide password" : "Show password"
										}
										onClick={() => setShowPassword(!showPassword)}
										className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute inset-y-0 right-0 flex cursor-pointer items-center rounded-r-md px-3 transition-colors focus-visible:ring-2 focus-visible:outline-none"
									>
										{/* Sized against the input's own text, not a 20px default. */}
										{showPassword ? (
											<IconEyeOff size={18} />
										) : (
											<IconEye size={18} />
										)}
									</button>
								</div>
								{errors.password && touched.password ? (
									<p className="text-error text-xs">{errors.password}</p>
								) : null}
							</div>

							{/* Options, not a field: a touch more air than the gap that
							    binds a label to its input. */}
							<div className="mt-1 flex flex-row items-center justify-between">
								<div className="flex items-center gap-2">
									<Checkbox
										id="rememberMe"
										checked={values.rememberMe}
										onCheckedChange={(checked) =>
											setFieldValue("rememberMe", checked === true)
										}
									/>
									<label
										htmlFor="rememberMe"
										className="cursor-pointer text-sm"
									>
										Remember this Device
									</label>
								</div>
								{/*
									Carries whatever is in the email field across.

									Somebody clicks this *because* the password did not work,
									which means they have already typed their address once.
									Asking for it again on the next screen is asking them to
									prove they know their own email.
								*/}
								<Link
									href={
										values.email.trim()
											? `/auth/forgot-password?email=${encodeURIComponent(
													values.email.trim(),
												)}`
											: "/auth/forgot-password"
									}
									className="text-primary cursor-pointer text-sm font-medium no-underline hover:underline"
								>
									Forgot Password ?
								</Link>
							</div>

							{/* The one element that submits gets its own breathing room. */}
							<div className="mt-3">
								<Button
									size="lg"
									className="w-full cursor-pointer"
									type="submit"
									disabled={isSubmitting || isLoading}
								>
									{isSubmitting || isLoading ? "Signing in..." : "Sign In"}
								</Button>
							</div>
						</div>
					</Form>
				)}
			</Formik>

			{subtitle}
		</LoadingOverlayComponent>
	);
};

export default AuthLogin;
